import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Label } from '@trussworks/react-uswds';
import classNames from 'classnames';
import { mdiChevronDown, mdiChevronRight, mdiChevronUp, mdiClose, mdiDomain } from '@mdi/js';
import { useController, useFormContext } from 'react-hook-form';
import Icon from '@components/icon/Icon';
import { organizationTypes, getOrganizationValue, mapOrganizationToOption } from './modals/profile/profileHelper';
import './usaceOrganization.scss';

type OrganizationType = (typeof organizationTypes)[keyof typeof organizationTypes];
type MenuPlacement = 'bottom' | 'top';

interface OrganizationInput {
  value?: number | string;
  id?: number | string;
  areaOfInterestId?: number | string;
  label?: string;
  name?: string;
  description?: string;
  type?: OrganizationType;
  parentOrgId?: number | string;
  parentId?: number | string;
  parentAreaOfInterestId?: number | string;
  [key: string]: unknown;
}

interface OrganizationOption extends OrganizationInput {
  value: number | string;
  label: string;
  type: OrganizationType;
}

interface UsaceOrganizationProps {
  name?: string;
  headquartersOption?: OrganizationInput | null;
  divisionOptions?: OrganizationInput[];
  districtOptions?: OrganizationInput[];
  // eslint-disable-next-line react/boolean-prop-naming
  disabled?: boolean;
  // eslint-disable-next-line react/boolean-prop-naming
  required?: boolean;
}

const getSelectedOrganizationValue = getOrganizationValue as (organization: OrganizationInput) => number | string;
const mapToOrganizationOption = mapOrganizationToOption as (
  organization: OrganizationInput | null | undefined,
  type?: OrganizationType
) => OrganizationOption | null;

const isSelected = (selectedIds: Set<number>, option: OrganizationOption) => selectedIds.has(Number(option.value));

const UsaceOrganization = ({
  name = 'usaceOrganizations',
  headquartersOption,
  divisionOptions = [],
  districtOptions = [],
  disabled,
  required,
}: UsaceOrganizationProps) => {
  const { control, trigger } = useFormContext();

  const {
    field,
    fieldState: { error },
  } = useController({ name, control });

  const hasError = Boolean(error);

  const labelStyles = classNames({
    required,
    optional: !required,
  });

  const selectRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedDivisionIds, setExpandedDivisionIds] = useState<Set<number>>(new Set());
  const [menuPlacement, setMenuPlacement] = useState<MenuPlacement>('bottom');

  const getHq = useMemo(
    () => mapToOrganizationOption(headquartersOption, organizationTypes.headquarters),
    [headquartersOption]
  );

  const getDivisionOptions = useMemo(
    () =>
      divisionOptions
        .map((division) => mapToOrganizationOption(division, organizationTypes.division))
        .filter(Boolean) as OrganizationOption[],
    [divisionOptions]
  );

  const getDistrictOptions = useMemo(
    () =>
      districtOptions
        .map((district) => mapToOrganizationOption(district, organizationTypes.district))
        .filter(Boolean) as OrganizationOption[],
    [districtOptions]
  );

  const allOptionsById = useMemo(() => {
    return [...(getHq ? [getHq] : []), ...getDivisionOptions, ...getDistrictOptions].reduce((optionsMap, option) => {
      optionsMap.set(Number(option.value), option);
      return optionsMap;
    }, new Map<number | string, OrganizationOption>());
  }, [getHq, getDivisionOptions, getDistrictOptions]);

  const selectedOrganizationList = useMemo(() => {
    const selectedOrganizations = (field.value ?? []) as OrganizationInput[];

    return selectedOrganizations
      .map((selected) => {
        const selectedValue = getSelectedOrganizationValue(selected);

        return allOptionsById.get(selectedValue) ?? mapToOrganizationOption(selected, selected?.type);
      })
      .filter(Boolean) as OrganizationOption[];
  }, [field.value, allOptionsById]);

  const selectedIds = useMemo(
    () => new Set(selectedOrganizationList.map((option) => Number(option.value))),
    [selectedOrganizationList]
  );

  const isHQSelected = getHq && selectedIds.has(Number(getHq.value));

  const districtsByDivisionId = useMemo(() => {
    return getDistrictOptions.reduce((districtMap, district) => {
      const parentOrgId = Number(district.parentOrgId);
      const districts = districtMap.get(parentOrgId) ?? [];
      districtMap.set(parentOrgId, districts.concat(district));
      return districtMap;
    }, new Map<number | string, OrganizationOption[]>());
  }, [getDistrictOptions]);

  const updateSelection = (nextSelection: OrganizationOption[]) => {
    field.onChange(nextSelection);
    setSearch('');
    trigger(name);
  };

  const removeSelectedOrganization = (optionToRemove: OrganizationOption) => {
    updateSelection(
      selectedOrganizationList.filter((selected) => Number(selected.value) !== Number(optionToRemove.value))
    );
  };

  const clearSelectedOrganizations = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    updateSelection([]);
  };

  const handleToggle = (option: OrganizationOption) => {
    if (disabled || !option) {
      return;
    }

    if (option.type === organizationTypes.headquarters) {
      updateSelection(isSelected(selectedIds, option) ? [] : [option]);
      return;
    }

    if (option.type === organizationTypes.division) {
      if (isSelected(selectedIds, option)) {
        updateSelection(selectedOrganizationList.filter((selected) => Number(selected.value) !== Number(option.value)));
        return;
      }
      const childDistrictIds = new Set(
        (districtsByDivisionId.get(Number(option.value)) ?? []).map((district) => Number(district.value))
      );
      updateSelection(
        selectedOrganizationList
          .filter((selected) => selected.type !== organizationTypes.headquarters)
          .filter((selected) => !childDistrictIds.has(Number(selected.value)))
          .concat(option)
      );
      return;
    }

    if (option.type === organizationTypes.district) {
      if (isSelected(selectedIds, option)) {
        updateSelection(selectedOrganizationList.filter((selected) => Number(selected.value) !== Number(option.value)));
        return;
      }
      updateSelection(
        selectedOrganizationList
          .filter((selected) => selected.type !== organizationTypes.headquarters)
          .filter((selected) => Number(selected.value) !== Number(option.parentOrgId))
          .concat(option)
      );
    }
  };

  const isOptionDisabled = (option: OrganizationOption) => {
    if (disabled) {
      return true;
    }
    if (isHQSelected && option.type !== organizationTypes.headquarters) {
      return true;
    }
    if (option.type === organizationTypes.district && selectedIds.has(Number(option.parentOrgId))) {
      return true;
    }
    return false;
  };

  const toggleDivisionExpanded = (divisionId: number | string) => {
    setExpandedDivisionIds((currentIds) => {
      const nextIds = new Set(currentIds);
      const convertedDivisionId = Number(divisionId);
      if (nextIds.has(convertedDivisionId)) {
        nextIds.delete(convertedDivisionId);
      } else {
        nextIds.add(convertedDivisionId);
      }
      return nextIds;
    });
  };

  const updateMenuPlacement = () => {
    const rect = selectRef.current?.getBoundingClientRect();
    if (!rect) {
      return;
    }
    const footerBuffer = 120;
    const menuHeight = 240;
    const spaceBelow = window.innerHeight - rect.bottom - footerBuffer;
    const spaceAbove = rect.top;
    setMenuPlacement(spaceBelow < menuHeight && spaceAbove > spaceBelow ? 'top' : 'bottom');
  };

  const openMenu = () => {
    updateMenuPlacement();
    setIsOpen(true);
  };

  const closeMenu = () => {
    setSearch('');
    setIsOpen(false);
    field.onBlur();
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      closeMenu();
    }
  };

  const handleCaretClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    if (isOpen) {
      closeMenu();
      return;
    }

    updateMenuPlacement();
    setIsOpen(true);
  };

  const matchesSearch = (option: OrganizationOption) => option.label?.toLowerCase().includes(search.toLowerCase());
  const visibleDivisions = getDivisionOptions.filter((division) => {
    const childDistricts = districtsByDivisionId.get(Number(division.value)) ?? [];

    return !search || matchesSearch(division) || childDistricts.some(matchesSearch);
  });

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleOutsideClick = (event: MouseEvent) => {
      if (!selectRef.current?.contains(event.target as Node)) {
        setSearch('');
        setIsOpen(false);
        field.onBlur();
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isOpen, field]);

  return (
    <div
      id={name}
      className={classNames('usace-organization-select', {
        'usace-organization-select--error': hasError,
      })}
      ref={selectRef}
    >
      <Label htmlFor={`${name}-search`} className={labelStyles}>
        <span id={`${name}_label`}>Select the USACE organization(s) you support</span>
      </Label>
      <div className='usace-organization-select__control' onClick={openMenu}>
        <div className='usace-organization-select__selected-values'>
          {selectedOrganizationList.map((organization) => (
            <span key={organization.value} className='usace-organization-select__chip'>
              {organization.label}
              <button
                type='button'
                className='usace-organization-select__chip-remove'
                aria-label={`Remove ${organization.label}`}
                disabled={disabled}
                onClick={(event) => {
                  event.stopPropagation();
                  removeSelectedOrganization(organization);
                }}
              >
                <Icon focusable={false} path={mdiClose} size={0.65} />
              </button>
            </span>
          ))}

          <input
            id={`${name}-search`}
            className='usace-organization-select__search'
            type='search'
            placeholder={selectedOrganizationList.length ? '' : 'Search organizations...'}
            value={search}
            disabled={disabled}
            aria-expanded={isOpen}
            aria-controls={`${name}-menu`}
            aria-invalid={hasError}
            onFocus={openMenu}
            onBlur={field.onBlur}
            onKeyDown={handleSearchKeyDown}
            onChange={(event) => {
              setSearch(event.target.value);
              openMenu();
            }}
          />
        </div>

        <div className='usace-organization-select__indicators'>
          {selectedOrganizationList.length > 0 && (
            <button
              type='button'
              className='usace-organization-select__clear-all'
              aria-label='Clear selected USACE organizations'
              disabled={disabled}
              onClick={clearSelectedOrganizations}
            >
              <Icon focusable={false} path={mdiClose} size={0.75} />
            </button>
          )}

          <button
            type='button'
            className='usace-organization-select__caret'
            aria-label={isOpen ? 'Close USACE organization options' : 'Open USACE organization options'}
            aria-expanded={isOpen}
            disabled={disabled}
            onClick={handleCaretClick}
          >
            <Icon focusable={false} path={isOpen ? mdiChevronUp : mdiChevronDown} size={0.9} />
          </button>
        </div>
      </div>
      {isOpen && (
        <div
          id={`${name}-menu`}
          className={classNames('usace-organization-select__menu', {
            'usace-organization-select__menu--top': menuPlacement === 'top',
          })}
        >
          <div className='usace-organization-select__group-label'>Headquarters</div>
          {getHq && (
            <label className='usa-checkbox usace-organization-select__option usace-organization-select__headquarters-option'>
              <input
                className='usa-checkbox__input'
                type='checkbox'
                checked={isSelected(selectedIds, getHq)}
                disabled={isOptionDisabled(getHq)}
                onChange={() => handleToggle(getHq)}
              />
              <span className='usa-checkbox__label'>
                <span className='usace-organization-select__option-content'>
                  <Icon focusable={false} path={mdiDomain} size={0.85} />
                  <span>{getHq.label}</span>
                </span>
              </span>
            </label>
          )}
          <div className='usace-organization-select__section-divider' />
          <div className='usace-organization-select__group-label'>Divisions & Districts</div>
          {visibleDivisions.map((division) => {
            const childDistricts = districtsByDivisionId.get(division.value) ?? [];
            const isExpanded =
              expandedDivisionIds.has(Number(division.value)) ||
              selectedIds.has(Number(division.value)) ||
              childDistricts.some((district) => selectedIds.has(Number(district.value))) ||
              search;
            return (
              <div key={division.value}>
                <div className='usace-organization-select__division-row'>
                  {childDistricts.length > 0 ? (
                    <Button
                      type='button'
                      unstyled
                      className='usace-organization-select__expand'
                      aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${division.label}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        toggleDivisionExpanded(division.value);
                      }}
                    >
                      <Icon focusable={false} path={isExpanded ? mdiChevronDown : mdiChevronRight} size={0.85} />
                    </Button>
                  ) : (
                    <span className='usace-organization-select__expand-spacer' />
                  )}

                  <label className='usa-checkbox usace-organization-select__option usace-organization-select__division-option'>
                    <input
                      className='usa-checkbox__input'
                      type='checkbox'
                      checked={isSelected(selectedIds, division)}
                      disabled={isOptionDisabled(division)}
                      onChange={() => handleToggle(division)}
                    />

                    <span className='usa-checkbox__label'>{division.label}</span>
                  </label>
                </div>
                {isExpanded &&
                  childDistricts
                    .filter((district) => !search || matchesSearch(district))
                    .map((district) => (
                      <label
                        key={district.value}
                        className='usa-checkbox usace-organization-select__option usace-organization-select__district'
                      >
                        <input
                          className='usa-checkbox__input'
                          type='checkbox'
                          checked={isSelected(selectedIds, district)}
                          disabled={isOptionDisabled(district)}
                          onChange={() => handleToggle(district)}
                        />
                        <span className='usa-checkbox__label'>{district.label}</span>
                      </label>
                    ))}
              </div>
            );
          })}
          <div className='usace-organization-select__footer'>
            <span>{selectedOrganizationList.length} selected</span>
            <div>
              <Button type='button' unstyled className='margin-right-2' onClick={() => updateSelection([])}>
                Clear all
              </Button>
              <Button type='button' onClick={closeMenu}>
                Done
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default UsaceOrganization;
